// Story 8.3 (ADO-2012), Task 13.4 (card 10, D-27): the Order sent banner. A change was refused
// because it was made from a view of the cart before its latest send; the cart shown is now the
// same cart, emptied by that send (Enabler E8-G, ADO-3109, AC8), the "new cart" of the copy. The
// designer's copy, word for word (DV-3), with the sent time as the designer's "sent at 11:42": the
// time alone when it is today, the date added otherwise, in Dublin time (D-27, `formatSentTime`),
// never the viewer's zone. "Today" is the day the refusal was noted (`notedAt`), so rendering reads
// no clock. "Add to a new cart" puts the number the Shopper picked into the new cart; a refused
// removal has nothing to add, so no action (decided with the user).
//
// The side panel draws it `stacked` (the frame); the cart review page `adaptive` (decided with the
// user). It lasts until its action is tapped, the cart is changed again, or the Shopper leaves the
// page (the store and `ConflictsClearedOnLeave`).
//
// Focus: the row the Shopper was using went with the old cart, so when the banner appears focus
// moves to its action, or to the banner itself (focusable, `role="alert"`) for a removal. In the
// side panel the panel does it (`manageFocus={false}`), after its focus trap has moved focus in
// and noted where to return it on close; the banner would otherwise be overruled by the trap.
// "Add to a new cart" ends the banner once its add has landed (the store's `addToNewCart`), so the
// host is told (`onAddToNewCart`) and hands focus to the line's quantity once it is in the new
// cart, as "Add it back" does. A failed add leaves the banner, with the add's error in it.
//
// ADO-3257 (story D-6): a read of the cart that named a send made after the cart shown holds a
// notice with no product: the banner then shows card 10's title alone, with no text and no
// action, since no change of the Shopper's was refused, and it leaves focus where it is.

import { useLayoutEffect, useRef } from 'react';
import { Banner, type BannerLayout } from '../design-system';
import { formatSentTime } from '../shared/deliveryDayDisplay';
import { useBasketStore } from './useBasketStore';
import styles from './OrderSentBanner.module.css';

/** Moves focus to the Order sent banner inside `container`: its action, or the banner itself. */
export function focusOrderSentBanner(container: HTMLElement | null): void {
  const host = container?.querySelector<HTMLElement>('[data-order-sent-banner]');
  const target =
    host?.querySelector<HTMLElement>('[data-banner-action]') ??
    host?.querySelector<HTMLElement>('[role="alert"]');
  target?.focus({ preventScroll: true });
}

export function OrderSentBanner({
  layout,
  manageFocus = true,
  onAddToNewCart,
}: {
  readonly layout: Exclude<BannerLayout, 'inline'>;
  readonly manageFocus?: boolean;
  /** Called with the product's SKU and the add as "Add to a new cart" adds it, to place focus on
   *  its line once the add has put it in the cart. */
  readonly onAddToNewCart?: (skuCode: string, added: Promise<boolean>) => void;
}) {
  const notice = useBasketStore((s) => s.orderSent);
  const addToNewCart = useBasketStore((s) => s.addToNewCart);
  // The last add of the product failed: shown in the banner, which stays (card 10).
  const addError = useBasketStore((s) =>
    s.orderSent?.skuCode == null ? undefined : s.mutationErrors.get(s.orderSent.skuCode),
  );
  const hostRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    // ADO-3257 (story D-6): a read's notice took no row from the Shopper, so focus stays.
    if (notice === null || notice.skuCode === null || !manageFocus) return;
    focusOrderSentBanner(hostRef.current?.parentElement ?? null);
  }, [notice, manageFocus]);

  if (notice === null) return null;
  const { quantity, skuCode, productName } = notice;
  return (
    <div ref={hostRef} data-order-sent-banner="">
      <Banner
        type="warning"
        layout={layout}
        title={`This order was sent at ${formatSentTime(notice.sentAt, notice.notedAt)}`}
        {...(quantity === null || skuCode === null
          ? {}
          : {
              action: {
                label: 'Add to a new cart',
                onClick: () => {
                  onAddToNewCart?.(skuCode, addToNewCart());
                },
              },
            })}
      >
        {productName === null ? null : (
          <>
            {`Your change to ${productName} wasn't saved, because the order had already gone.`}
            {addError === undefined ? null : (
              <span role="alert" className={styles.error}>
                {addError}
              </span>
            )}
          </>
        )}
      </Banner>
    </div>
  );
}
